// The handover (F504, decision 112): a few lines from the pilot who flew
// to the pilot who comes next, written on the sheet of the last sector.
// Offered after a return and on the airframe's page to the pilot who has
// the aircraft or who returned it last; the platform decides who may.
// Nothing to say is an answer too (decision 120): the platform writes its
// own line, and the page stops asking once the sheet carries a handover
// since the last return.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, unwrap } from "@/api";
import { ErrorNote } from "@/components/ErrorNote";
import { useHandoverAt } from "@/components/FlightSteps";

export function HandoverForm({ airframeId, reg, compact = false }: { airframeId: string; reg?: string; compact?: boolean }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(!compact);
  const [text, setText] = useState("");
  const [written, setWritten] = useState<"note" | "nothing" | null>(null);
  const settle = (kind: "note" | "nothing") => {
    setText("");
    setWritten(kind);
    setOpen(false);
    queryClient.invalidateQueries({ queryKey: ["tech-log", airframeId] });
    queryClient.invalidateQueries({ queryKey: ["maintenance", airframeId] });
    queryClient.invalidateQueries({ queryKey: ["airframe", airframeId] });
  };
  const write = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/airframes/{id}/handover", { params: { path: { id: airframeId } }, body: { text: text.trim() } })),
    onSuccess: () => settle("note"),
  });
  const nothing = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/airframes/{id}/handover", { params: { path: { id: airframeId } }, body: { text: "", nothing: true } })),
    onSuccess: () => settle("nothing"),
  });
  if (!open) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid="handover">
        {written ? (
          <span className="text-ok text-sm" data-testid="handover-written">
            {written === "nothing" ? "Nothing to hand over, noted." : "Handover written for the next pilot."}
          </span>
        ) : null}
        <button type="button" className="btn btn-sm" onClick={() => setOpen(true)} data-testid="handover-open">
          {written ? "Write more" : "Write the handover"}
        </button>
        {!written ? (
          <button type="button" className="btn btn-sm" onClick={() => nothing.mutate()} disabled={nothing.isPending} data-testid="handover-nothing" title="The next pilot reads that you had nothing to report">
            Nothing to hand over
          </button>
        ) : null}
        <ErrorNote error={nothing.error} />
      </div>
    );
  }
  return (
    <form
      className="space-y-2"
      data-testid="handover"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim().length >= 3) write.mutate();
      }}
    >
      <label className="flex flex-col gap-1">
        <span className="font-semibold">Handover{reg ? ` of ${reg}` : ""}, for the next pilot</span>
        <span className="muted text-xs">What they should know before they fly: how the aircraft behaved, a defect you noticed, where you left it, what is left in the tanks. It goes on the sheet of your last sector and on their pre-flight.</span>
        <textarea className="input w-full" rows={4} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Left windshield heat slow to come on after start. 3,200 kg on arrival, chocks in at the stand." aria-label="The handover" data-testid="handover-text" />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" className="btn btn-sm btn-primary" disabled={write.isPending || text.trim().length < 3} data-testid="handover-submit">
          Write the handover
        </button>
        {compact ? (
          <button type="button" className="btn btn-sm" onClick={() => setOpen(false)}>
            Not now
          </button>
        ) : null}
        {!written ? (
          <button type="button" className="btn btn-sm" onClick={() => nothing.mutate()} disabled={nothing.isPending} data-testid="handover-nothing">
            Nothing to hand over
          </button>
        ) : null}
      </div>
      <ErrorNote error={write.error} />
      <ErrorNote error={nothing.error} />
    </form>
  );
}

/**
 * The handover on the airframe's Now card (decision 120; 2026-11-03: after
 * the upload, never during the flight, as the step guide says): for the
 * pilot of the last return until they have written it (a note or "nothing
 * to hand over") or someone else books.
 */
export function HandoverBlock({ airframeId, reg, meId, booked, last }: { airframeId: string; reg: string; meId: string; isHolder?: boolean; booked: boolean; last: { pilotId: string | null; at: string | null } }) {
  const returnedByMe = !booked && last.pilotId === meId && !!last.at;
  const handoverAt = useHandoverAt(airframeId, meId, returnedByMe);
  const written = returnedByMe && handoverAt !== null && new Date(handoverAt).getTime() >= new Date(last.at!).getTime();
  if (!returnedByMe || written) return null;
  return (
    <div className="mt-3" data-testid="handover-block">
      <HandoverCall airframeId={airframeId} reg={reg} />
    </div>
  );
}

/**
 * The call to write the handover (2026-11-03, Gianluca: "once everything
 * closes and you ask me to write notes, it is not visible"): a card in the
 * accent, the form open, on the flight's page after the return and on the
 * airframe's page, until the handover is written.
 */
export function HandoverCall({ airframeId, reg }: { airframeId: string; reg?: string }) {
  return (
    <section className="callout-accent space-y-2" data-testid="handover-call" aria-label="The handover for the next pilot">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="pill pill-accent">Now</span>
        <h2 className="text-[1.0714rem] font-semibold">Your handover for the next pilot</h2>
      </div>
      <p className="text-sm">The flight is in. A few lines on how the aircraft behaved, what you noticed and where you left it; or say there is nothing to hand over. The next pilot reads it on their pre-flight.</p>
      <HandoverForm airframeId={airframeId} reg={reg} />
    </section>
  );
}
